import * as THREE from 'three';
import {MemoCache} from 'shared/Cache';
import {CabinetMesh} from 'components/customer/RoomPlanner/types.d';
import {
    computeWorldBoundsExcludingAnimatedDoorParts,
    getDirection,
    getFacingRotation,
    isAdjustableLeg,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    isCornerLShapedCabinet,
    isNonRectShapeProduct,
} from 'components/customer/RoomPlanner/helpers/shapeProducts';
import {
    componentRoleOf,
    isSolidRole,
} from 'components/customer/RoomPlanner/helpers/componentRoles';
import {
    collisionBoxes,
    ExteriorFace,
    exteriorFaces,
    extrudedBoxes,
    isBoxShaped,
} from 'components/customer/RoomPlanner/helpers/componentGeometry';
import {
    PairFacts,
    ProductCache,
    ProductFacts,
    SnapContext,
} from 'components/customer/RoomPlanner/helpers/snapping/types';

export interface SnapOptions {
    camera: THREE.Camera;
    overlappingCabinets?: CabinetMesh[] | null;
    wallGroup?: THREE.Object3D | null;
    products?: ProductCache;
}

/**
 * The context every snapping algorithm is handed for one drag.
 * @param {THREE.Group} cabinetGroup The dragged product.
 * @param {THREE.Vector3} targetPos Where the drag puts it.
 * @param {THREE.Group[]} productsRef Every product in the room.
 * @param {SnapOptions} options The camera, overlaps, walls and, optionally,
 * the caller's registry of the products' facts. Without one, the context
 * gets its own, so nothing measured outlives it.
 * @return {SnapContext} The context.
 */
export const createSnapContext = (
    cabinetGroup: THREE.Group,
    targetPos: THREE.Vector3,
    productsRef: THREE.Group[],
    {
        camera,
        overlappingCabinets = null,
        wallGroup,
        products = new ProductRegistry(),
    }: SnapOptions
): SnapContext => {
    const currentPos = new THREE.Vector3();
    cabinetGroup.getWorldPosition(currentPos);

    return {
        selected: products.get(cabinetGroup),
        products,
        targetPos,
        currentPos,
        productsRef,
        overlappingCabinets,
        camera,
        wallGroup,
    };
};

/**
 * An three.js object bounding box cache, each measured on first ask.
 * Shared, so readonly.
 */
class Object3DBBoxCache extends MemoCache<THREE.Object3D, THREE.Box3> {
    /**
     * @param {THREE.Object3D} object3D to create a bounding box for.
     * @return {THREE.Box3} Its bounding box in world coordinates.
     */
    protected create(object3D: THREE.Object3D): THREE.Box3 {
        return new THREE.Box3().setFromObject(object3D);
    }
}

/**
 * @param {THREE.Group} group A placeholder.
 * @return {THREE.Box3} Its box without its arrows and measurements, or its
 * whole box when its meshes give none.
 */
const placeholderBBox = (group: THREE.Group): THREE.Box3 => {
    const bbox = computeWorldBoundsExcludingAnimatedDoorParts(group);

    return !bbox.isEmpty() &&
        Number.isFinite(bbox.min.x) &&
        Number.isFinite(bbox.max.x)
        ? bbox
        : new THREE.Box3().setFromObject(group);
};

/**
 * @param {THREE.Object3D} component A product's component.
 * @return {boolean} True unless the Fronts switch hides it, or a group it is
 * in (doorFronts.ts). Wireframe hides components too, but they still count.
 */
export const isShown = (component: THREE.Object3D): boolean => {
    for (
        let node: THREE.Object3D | null = component;
        node;
        node = node.parent
    ) {
        if (node.userData?.frontsHidden) return false;
    }

    return true;
};

/**
 * @param {THREE.Box3[]} boxes A product's components, its fronts shut.
 * @return {THREE.Box3[]} Each stood its full height, carcase to top, so the
 * space inside is solid and its faces inside aren't faces (CO-5961).
 */
const closedOff = (boxes: THREE.Box3[]): THREE.Box3[] => {
    const low = Math.min(...boxes.map(({min}) => min.y));
    const high = Math.max(...boxes.map(({max}) => max.y));

    return boxes.map(
        ({min, max}) =>
            new THREE.Box3(
                new THREE.Vector3(min.x, low, min.z),
                new THREE.Vector3(max.x, high, max.z)
            )
    );
};

/**
 * @param {THREE.Mesh} mesh A component.
 * @return {boolean} True for a front, the carcase or one no rule knows.
 */
const isSolidComponent = (mesh: THREE.Mesh): boolean =>
    isSolidRole(componentRoleOf(mesh));

/**
 * What routing and the strategies read about one product, worked out once.
 * The box and flags are read when it is described; component and leg boxes when
 * first asked for, and kept. Every box is shared, so read only.
 */
export class DescribedProduct implements ProductFacts {
    readonly bbox: THREE.Box3;
    readonly size: THREE.Vector3;
    readonly direction: ProductFacts['direction'];
    readonly isUpper: boolean;
    readonly isBulkhead: boolean;
    readonly isFloorProduct: boolean;
    readonly isKickLength: boolean;
    readonly isNonManualPanel: boolean;
    readonly isFiller: boolean;
    readonly sitsOnFloor: boolean;
    readonly isCornerLShape: boolean;
    readonly isNonRectShape: boolean;
    readonly isReturn: boolean;
    readonly isRightReturn: boolean;
    readonly ignoresSnapping: boolean;
    readonly isPlaceholder: boolean;

    private readonly componentBBoxes = new Object3DBBoxCache();
    // Measured on first read: it boxes every leg, and only kicker frame and
    // kick-length panel snaps need them.
    private measuredLegBBoxes?: THREE.Box3[];
    // Worked out on first read, for dragging (CO-5851): only face alignment
    // and butting need them.
    private measuredSolidBBoxes?: THREE.Box3[];
    private measuredExteriorFaces?: ExteriorFace[];
    private measuredBodyBBoxes?: THREE.Box3[];
    private shownComponents?: THREE.Mesh[];

    /**
     * @param {THREE.Group} group The product, at its current position.
     */
    constructor(readonly group: THREE.Group) {
        const {userData} = group;

        this.isPlaceholder = Boolean(userData.isPlaceholder);
        this.bbox = this.isPlaceholder
            ? placeholderBBox(group)
            : new THREE.Box3().setFromObject(group);
        this.size = this.bbox.getSize(new THREE.Vector3());
        // A placeholder is a Y-up model, turned about y (getFacingRotation).
        this.direction = getDirection(getFacingRotation(group));
        this.isUpper = Boolean(userData.isUpperProduct);
        this.isBulkhead = Boolean(userData.isBulkheadComponent);
        this.isFloorProduct = Boolean(userData.isFloorProduct);
        this.isKickLength = Boolean(userData.isKickerLengthProduct);
        this.isNonManualPanel = Boolean(userData.isNonManualPanel);
        this.isFiller = Boolean(
            userData.isManualPanel ||
            userData.isPanelProduct ||
            userData.isVerminProduct ||
            userData.isFillerProduct
        );
        this.sitsOnFloor = Boolean(userData.sitOnFloor);
        this.isCornerLShape = isCornerLShapedCabinet(group);
        this.isNonRectShape = isNonRectShapeProduct(group);
        this.isReturn = Boolean(userData.isReturnProduct);
        this.isRightReturn = Boolean(userData.isRightReturnProduct);
        this.ignoresSnapping = Boolean(userData.ignoreSnapping);
    }

    /**
     * @return {THREE.Box3[]} The adjustable legs' boxes, measured on first
     * read.
     */
    get legBBoxes(): readonly THREE.Box3[] {
        if (this.measuredLegBBoxes === undefined) {
            const legs: THREE.Box3[] = [];
            this.group.updateWorldMatrix(true, true);
            this.group.traverse((child) => {
                if (isAdjustableLeg(child))
                    legs.push(this.componentBBox(child));
            });
            this.measuredLegBBoxes = legs;
        }

        return this.measuredLegBBoxes;
    }

    /**
     * @return {boolean} True when it stands on at least one adjustable leg.
     * Legs load asynchronously, so a cabinet reads as legless until they
     * arrive.
     */
    get hasLegs(): boolean {
        return this.legBBoxes.length > 0;
    }

    /**
     * @return {THREE.Box3[]} The boxes of its shown solid components, the
     * fronts, carcase and those no role rule knows (componentRoles.ts),
     * closed off while its fronts are shut. With none, its own box.
     */
    get solidBBoxes(): readonly THREE.Box3[] {
        this.measuredSolidBBoxes ??= this.orOwnBox(
            this.closedWhenShut(
                this.components
                    .filter(isSolidComponent)
                    .flatMap(this.solidBoxesOf)
            )
        );

        return this.measuredSolidBBoxes;
    }

    /**
     * @return {THREE.Box3[]} What a drag collides with: every shown
     * component, its solid ones closed off while its fronts are shut
     * (CO-5961). With none, its own box.
     */
    get bodyBBoxes(): readonly THREE.Box3[] {
        const solid = this.components.filter(isSolidComponent);
        const rest = this.components.filter((mesh) => !isSolidComponent(mesh));
        this.measuredBodyBBoxes ??= this.orOwnBox([
            ...this.closedWhenShut(solid.flatMap(collisionBoxes)),
            ...rest.flatMap(collisionBoxes),
        ]);

        return this.measuredBodyBBoxes;
    }

    /**
     * @return {THREE.Mesh[]} Its shown components, world matrices current,
     * found once.
     */
    private get components(): THREE.Mesh[] {
        if (this.shownComponents === undefined) {
            this.group.updateWorldMatrix(true, true);
            const meshes: THREE.Mesh[] = [];
            this.group.traverse((node) => {
                if ((node as THREE.Mesh).isMesh)
                    meshes.push(node as THREE.Mesh);
            });
            this.shownComponents = meshes.filter(isShown);
        }

        return this.shownComponents;
    }

    /**
     * @param {THREE.Box3[]} boxes Its components' boxes.
     * @return {THREE.Box3[]} Closed off while it has fronts shown and shut.
     */
    private closedWhenShut = (boxes: THREE.Box3[]): THREE.Box3[] =>
        !this.group.userData.isDoorOrDrawerOpen &&
        this.components.some((mesh) => componentRoleOf(mesh) === 'front')
            ? closedOff(boxes)
            : boxes;

    /**
     * @param {THREE.Box3[]} boxes Its components' boxes.
     * @return {THREE.Box3[]} Those, or its own box when there are none.
     */
    private orOwnBox = (boxes: THREE.Box3[]): THREE.Box3[] =>
        boxes.length ? boxes : [this.bbox];

    // A box-shaped component's box, or the boxes a shaped one, such as an
    // L-shape panel, cuts into, so its faces stop at its cut-out. The
    // selection outline's box is empty, so it is left out.
    private solidBoxesOf = (mesh: THREE.Mesh): THREE.Box3[] =>
        isBoxShaped(mesh)
            ? [this.componentBBox(mesh)].filter((bbox) => !bbox.isEmpty())
            : (extrudedBoxes(mesh) ?? []);

    /**
     * @return {ExteriorFace[]} The upright faces of its solid body that can
     * be seen from outside it, worked out on first read.
     */
    get exteriorFaces(): readonly ExteriorFace[] {
        this.measuredExteriorFaces ??= exteriorFaces(this.solidBBoxes);

        return this.measuredExteriorFaces;
    }

    /**
     * @param {THREE.Object3D} component One of the product's components.
     * @return {THREE.Box3} Its world box, measured on first ask.
     */
    componentBBox(component: THREE.Object3D): THREE.Box3 {
        return this.componentBBoxes.get(component);
    }
}

/**
 * Every product's facts for one calculation: each product is described when
 * first asked for and its facts kept, so it is measured once. It lasts as
 * long as whoever holds it, across an await too; make one per calculation and
 * drop it after, since nothing updates facts when a product moves.
 */
export class ProductRegistry extends MemoCache<THREE.Group, ProductFacts> {
    /**
     * @param {THREE.Group} group A product in the room.
     * @return {ProductFacts} Its facts at its current position.
     */
    protected create(group: THREE.Group): ProductFacts {
        return new DescribedProduct(group);
    }
}

/**
 * @param {SnapContext} ctx The calculation, for the dragged product's facts
 * and to describe the other.
 * @param {THREE.Group} other The product it may snap to.
 * @return {PairFacts} The facts one routing decision is made on.
 */
export const describePair = (
    ctx: SnapContext,
    other: THREE.Group
): PairFacts => {
    const selectedFacts = ctx.selected;
    const otherFacts = ctx.products.get(other);

    return {
        selected: selectedFacts,
        other: otherFacts,
        hasMatchingTopY:
            Math.abs(selectedFacts.bbox.max.y - otherFacts.bbox.max.y) < 5,
        sameDirection: selectedFacts.direction === otherFacts.direction,
    };
};
